import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Menu class recipes — the action menu ("more actions"), not the select's option list.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-menu-*` slots, which `.ds-menu` sets per theme in `components/styles/design-system.css`.
 * Read the block header there first — three of those slots are EXTRAPOLATED rather than authored,
 * because the drawn `Menu` component is a selection list with no divider and no destructive row.
 *
 * Geometry is read off the `Menu` component (node 556:20625) and matches the select's list exactly,
 * which is expected: Figma draws one `Menu` component, and the select's popup (node 556:20985) and
 * this panel are both instances of it. Its rows are instances of `List-Item` (node 82:7424).
 *
 * Re-read on 2026-09-15 after the `List-Item` rework, which moved every number in both the panel and
 * the row. What changed, and where the values come from, is documented on each recipe below.
 *
 * ── Re-read again on 2026-09-22 ────────────────────────────────────────────────────────────────
 *
 * `Menu` is no longer a COMPONENT_SET. The old master id (`556:20624`) resolves to `null` — deleted
 * outright rather than renamed-superseded, which is this file's usual habit — and the current
 * component is a plain COMPONENT at `556:20625` on page "Menu [Reviewed]✅". Its own description
 * gives the reason: the `Selection = Single-select / Multi-select` axis is gone, because the two
 * variants differed only in the `Type` they set on their child rows and `Type` belongs to
 * `List-Item`. Its API is now a `Content` SLOT plus a `Scrollbar` boolean.
 *
 * Nothing in this file modelled that axis, so the removal costs no code. Two drawn numbers DID move
 * and are ported below: the panel's vertical padding (10px → 8px) and the row's radius (a pill →
 * 10px). See each recipe.
 */

/**
 * The panel.
 *
 * `ds-menu` is applied by the component rather than inherited, for the same reason `Select` applies
 * `ds-select`: the popup is portalled into `<body>`, so it has none of the trigger's ancestors and
 * would otherwise resolve every slot to its light-mode fallback. The theme class goes on the
 * `Portal` instead, which IS an ancestor of this element, so `:where(.dark) .ds-menu` still reaches
 * it.
 *
 * Width is content-driven with a 220px floor, matching Select's shared panel. The select also caps
 * its menu at the field width; this action menu cannot use that cap because its anchor may be a 40px
 * icon button. 220px is the row width the component draws (node 556:20713), used as a minimum so
 * a long label can widen the panel.
 *
 * `max-h-[--available-height]` plus scrolling keeps a long menu on screen near the bottom of the
 * viewport; Base UI computes the value from the collision boundary.
 *
 * ── The geometry, re-read on 2026-09-15 ────────────────────────────────────────────────────────
 *
 * Off the reworked master's bound variables, not measured off a render:
 *
 *   radius    `radius/md`  16px, was 20
 *   padding   `x2`  8px on all four sides — was 8 across and 10 down, and 12 / 16 before that
 *   row gap   `x0,5`  2px, was 0
 *
 * The vertical padding is the 2026-09-22 tweak. The component binds `x2` to all four sides now,
 * where it used to bind `x2,5` top and bottom, so the panel is 4px shorter and the first and last
 * rows sit the same distance from the panel edge as the rows do from its sides. It matters beyond
 * the 4px because the separator's negative inset MUST equal the horizontal padding (see
 * `menuSeparatorRecipe`), and having one padding value rather than two removes the way that
 * constraint previously went stale.
 *
 * The gap matters more than its size suggests: the rows carry their own rounded fill, so without
 * air between them two adjacent hover fills would touch and read as one block. The panel gap
 * separates an optional Search from the action list; the list wrapper carries the same gap between
 * rows. This also keeps the row spacing when no Search is present.
 *
 * The 220px FLOOR is deliberately unchanged, even though the master's frame is 320 wide. That frame
 * is `layoutSizingHorizontal: 'FILL'`, so 320 is whatever its parent happened to give it rather than
 * a specified width; the `List-Item` master's own frame is 220, which is the number this already
 * used. Pinning the action menu to 320 would widen every shipped menu on the strength of a
 * measurement the file does not actually make.
 */
export const menuPanelRecipe = cva([
  'ds-menu',
  'box-border flex w-max min-w-[220px] flex-col gap-[2px]',
  'ds-popup-scroll max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'p-[8px]',
  'rounded-[16px]',
  'border-solid border-[length:1px] border-[var(--ds-menu-border)]',
  'bg-[var(--ds-menu-bg)]',
  '[box-shadow:var(--effect-shadow-10)]',
  'outline-none',

  /**
   * ── The entrance and the exit ────────────────────────────────────────────────────────────────
   *
   * `--motion-overlay` (200ms) in, `--motion-overlay-exit` (150ms) out. An overlay is lighter than
   * a dialog and is opened far more often, so it moves faster than `--motion-surface` does; the
   * exit is faster again, because the user has already chosen and the panel is now in the way.
   *
   * `data-starting-style` and `data-ending-style` are Base UI's two lifecycle hooks — present for
   * one frame on mount and for the whole of the close respectively. The close hook also keeps the
   * panel mounted long enough to animate out: Base UI defers the unmount until the transitions on
   * the element have finished, which is why the exit needs no JavaScript here.
   *
   * TWO VARIANTS.
   *
   *   default          opacity 0 -> 1 AND scale 0.96 -> 1, from the anchor
   *   reduced motion   opacity only
   *
   * The fade is unconditional, the scale is `motion-safe:`. Reduced motion means gentler rather
   * than nothing: a cross-fade aids comprehension and triggers nobody, and a panel that simply
   * appears is a worse experience than one that fades. And `motion-safe:` rather than a
   * `motion-reduce:` suppression is a specificity fact, not a preference — a lifecycle-qualified
   * rule is (0,2,0) and a `motion-reduce:` rule inside a media query is (0,1,0), because a media
   * query contributes none. Suppression loses; see `docs/motion.md`.
   *
   * ORIGIN-AWARE, which is the part that distinguishes a popup from a modal. `--transform-origin`
   * is Base UI's own measurement of the point the panel is anchored to, so a menu opened BELOW a
   * trigger grows downward from its top edge and one flipped above it grows upward — the panel
   * appears to come out of the control that opened it. The CSS default of `center` would have every
   * menu grow symmetrically out of its own middle, which reads as an unattached card appearing
   * beside the button rather than as the button's own menu. The fallback keeps the declaration
   * valid if the library ever stops publishing the variable; `Motion (lifecycle)` asserts the real
   * value rather than trusting it.
   *
   * 0.96, not 0: nothing in the real world appears out of nothing.
   */
  '[transform-origin:var(--transform-origin,center)]',
  'transition-[opacity,scale] duration-[var(--motion-overlay)] ease-[var(--motion-overlay-ease)]',
  'data-[ending-style]:duration-[var(--motion-overlay-exit)]',
  'data-[ending-style]:ease-[var(--motion-overlay-exit-ease)]',
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  'motion-safe:data-[starting-style]:scale-[0.96] motion-safe:data-[ending-style]:scale-[0.96]',
]);

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 * The z-index belongs HERE rather than on the panel, because this is the element Base UI gives
 * `position: fixed`; on the panel it would have nothing to stack against.
 *
 * `z-[1400]` sits above MUI's modal (1300) and drawer (1200) and below its tooltip (1500) — the
 * same value and the same reasoning as `selectPositionerRecipe`. An action menu inside a legacy MUI
 * `Dialog` is an ordinary case during the refresh, and a popup trapped behind its own dialog is
 * unusable. The value is MUI's scale rather than this layer's because MUI is what it coexists with.
 */
export const menuPositionerRecipe = cva(['z-[1400]']);

/**
 * A row — the reworked Figma `List-Item` (node 82:7424), which this and the select's options share.
 *
 * Figma `Label/lg` — 14px Medium on a 20px line, truncating to one line — with 12px across and 8px
 * down, which is the design's 36px row. It was 6px down and a 32px row; `x2` is what the reworked
 * cells bind. The select's options use the same cell, so the two moved together.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation, because Base UI highlights on
 * hover by default. That is what the cells want: one hover treatment for both. `State=Focus` IS a
 * separate cell, and it is a ring rather than a fill — see the focus block below.
 *
 * There is deliberately no selected state. The drawn component has one — it is a selection list, and
 * the rework gave it back a trailing tick — but an action menu has nothing to select: each row runs a
 * command and the panel closes. `Select` renders that half of the cell; see `selectItemCheckRecipe`.
 *
 * The `Type=Multi Select` half of the cell has no home here either, and that is the one part of the
 * rework not reproduced anywhere in code: it draws a leading checkbox, which needs a multi-select
 * list, and neither component is one. Flagged rather than approximated.
 *
 * ── The RADIUS, re-read 2026-09-22: one constant, no longer an axis ────────────────────────────
 *
 * All sixteen cells now draw a **10px** corner. It used to be a pill (`radius/full`), and this file
 * carried a `multiline` axis to keep a described row off that pill — a 60px row with a pill radius
 * reads as a capsule, so the described row derived 8px from a concentric rule (the panel's 16px
 * radius minus the 8px of padding between the row's fill and the panel's edge).
 *
 * 10px removes the reason for the axis. There is no capsule to avoid at any row height, so a
 * described row and a single-line row can take the same corner, and they do — which is also the
 * only reading the drawing supports, since the file has no described cell to say otherwise.
 *
 * Note that 10 is NOT the concentric answer, which is still 8. The design has simply chosen a
 * number; this reproduces the drawing rather than the derivation, and the derivation is recorded
 * here only so the next reader does not re-derive 8 and "fix" this.
 *
 * DESIGN-SYSTEM GAP: the 10 is a **raw, unbound literal** on every one of the sixteen cells. The
 * `⭕️ Radius` collection has `sm` 6, `md` 16, `lg` 24 and `full`, and 10 is none of them. The
 * component's own description is also stale against its drawing — it still says the container is
 * `radius/lg` (24) and the rows are `radius/sm` (6), where the file actually draws 16 and 10.
 * Reproduced as drawn and raised with design; if 10 was a slip, this is the single line to
 * change.
 *
 * Selector precedence is kept mutually exclusive rather than order-dependent, as in the sibling
 * recipes: disabled wins over hovered by excluding it, so which rule applies never depends on
 * utility sort order.
 */
export const menuItemRecipe = cva(
  [
    'group/mi',
    'relative flex w-full shrink-0 items-center gap-[8px]',
    'm-0 appearance-none border-0 bg-transparent text-left',
    'px-[12px] py-[8px]',
    'rounded-[10px]',
    'font-sans text-label-lg select-none',
    'outline-none',
    'cursor-pointer',
    /**
     * No transition, on purpose. `data-highlighted` moves under the arrow keys as well as the
     * pointer, and a 150ms tint made the highlight TRAIL the keyboard — the row being left was still
     * fading out when the next key landed, so a fast run down the list smeared across three rows.
     * A menu is opened constantly and navigated faster than any fade can keep up with; the
     * highlight is feedback, and feedback that lags the key is worse than none. `selectItemRecipe`
     * makes the same call for the same reason.
     */
    /**
     * The keyboard ring — the design system's SHARED two-shadow ring, which `Button`, `Checkbox`
     * and `Radio` all draw: `effect/focus-inner` spread 2 painted over `effect/focus-outer` spread
     * 4, so the inner band reads as a GAP and the visible ring is the outer band it uncovers.
     *
     * The inner part is substituted here; the outer part follows the shared effect role.
     *
     * ── The GAP is the panel's own fill, not `effect/focus-inner` ───────────────────────────────
     *
     * `effect/focus-inner` carries the two `background/primary` values — `--neutral-50` light,
     * `--neutral-900` dark — because the gap's whole job is to look like the surface the control
     * sits on. In light mode that role currently matches the menu's `background/control`; in dark
     * mode it does not. Reading the panel slot keeps the band correct in both themes and for any
     * caller-themed panel.
     *
     * So the gap reads `--ds-menu-focus-gap`, which the class points at its own panel fill. That is
     * `Radio`'s precedent rather than a new idea: `--ds-radio-focus-backdrop` exists for exactly
     * this reason, because a gap ring needs an opaque backdrop matching whatever it sits against.
     * `Button`'s own note says it plainly — the gap "is what holds the ring off a same-coloured
     * surface", which only works if it IS that surface's colour.
     *
     * This is also why the ring cannot be an offset `outline`, which was tried: an outline's gap is
     * transparent, and while that happens to look right on an opaque panel it cannot follow the
     * row's corner with a painted band, and it drops the system's geometry vocabulary for a
     * second technique doing the same job.
     *
     * The outer ring is `effect/focus-outer`: 50% in light, where it now clears 3:1 on this panel,
     * and the existing 40% in dark. `--ds-menu-focus` keeps that role countable in the slot harness.
     *
     * Inner listed FIRST so it paints on top of the wider outer one, which is the order
     * `FocusRing.stories` composes and the order every sibling recipe uses. Every length is a token,
     * so the ring cannot drift from the one the rest of the system draws. `outline-none` in the base
     * suppresses the browser's own ring, which would otherwise sit inside this one.
     */
    focusRing('menuItem'),

    // Tone-independent. What a disabled row looks like is per tone (see `tone` below), but the
    // pointer says the same thing either way.
    'data-[disabled]:cursor-not-allowed',
  ],
  {
    variants: {
      tone: {
        /**
         * Disabled RECOLOURS here, following the reworked cells, which author
         * `foreground/disabled/default` on the disabled rows rather than dimming them. An explicit
         * disabled colour is also what `contrast.md` asks for: a multiplier is a value no contrast
         * harness can read without compositing it first.
         *
         * The disabled cells keep the resting FILL, so there is no background rule to add — and
         * `data-[disabled]` already beats `data-[highlighted]` by exclusion, so a disabled row
         * cannot pick up a hover fill on the way past.
         */
        default: [
          'text-[var(--ds-menu-item-fg)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-hover)]',
          'data-[highlighted]:not-data-[disabled]:text-[var(--ds-menu-item-fg-hover)]',
          'data-[disabled]:text-[var(--ds-menu-item-fg-disabled)]',
          /**
           * The leading glyph, a step quieter than the label — the reworked cells hold it at
           * `foreground/secondary/default` while the label brightens on hover.
           *
           * Declared HERE rather than in `menuItemIconRecipe` because it is tone-specific: a
           * destructive row's glyph keeps the row's `currentColor`, which is what makes it red
           * without the caller knowing about tones. A descendant selector is what lets one axis
           * own both halves; the icon recipe stays tone-blind and keeps working for either.
           *
           * Dropped on a disabled row, so the glyph follows the label down — the disabled cells
           * draw both at `foreground/disabled/default`, and there the dimness IS the state.
           */
          'not-data-[disabled]:[&_[data-part="item-icon"]]:text-[var(--ds-menu-item-icon)]',
        ],
        /**
         * The destructive row keeps ONE label colour across resting and hovered, unlike a default
         * row's one-step brightening. The FILL is what signals the highlight here: a row tinted red
         * is unmistakably the highlighted one, so moving the label as well buys nothing and makes
         * the single destructive item on a panel read as two different reds as the cursor passes.
         *
         * It also keeps the DIMMING for its disabled state, where the default row now recolours.
         * Recolouring a destructive row to neutral grey would erase the one thing saying the action
         * is dangerous, and no cell exists for "disabled and destructive" anywhere in the library —
         * not in the reworked `List-Item` and not in `Button` — so there is nothing to be 1:1 with.
         * The dimming reads a SLOT rather than an `opacity-50` literal, so the harness can see it.
         */
        destructive: [
          'text-[var(--ds-menu-item-fg-destructive)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-destructive-hover)]',
          'data-[disabled]:opacity-[var(--ds-menu-disabled-opacity)]',
        ],
      },
    },
    defaultVariants: { tone: 'default' },
  }
);

/**
 * A row's leading glyph. Sized and boxed exactly as the select's option icons, so a 16px icon lands
 * the same way in both menus.
 *
 * TONE-BLIND on colour, which is a change in where the colour comes from rather than in what this
 * recipe does. It still resolves `currentColor` by default — the destructive row's glyph is red for
 * exactly that reason — but the `tone: default` axis on `menuItemRecipe` now overrides it to
 * `--ds-menu-item-icon` through a descendant selector, because the reworked cells hold the glyph a
 * step below the label rather than at it. Keeping the override on the AXIS and not here is what
 * lets one rule serve both tones; see the note beside it.
 *
 * The select's `selectItemIconRecipe` names its slot directly, because that component has no tones.
 */
/**
 * Sizes the row's leading glyph.
 *
 * TWO rules, because there are two kinds of glyph here and neither rule reaches both — the same
 * split `buttonIconRecipe` needs (PR #1130), in the Menu's own copy of the defect.
 *
 *   - `[&_svg]:size-full` sizes a RAW svg, which has no font-size relationship to inherit and is
 *     sized by its own `width`/`height` attributes instead.
 *   - `text-[length:16px]` sizes a MUI-backed glyph, i.e. anything out of `icons/`. `SvgIcon` draws
 *     itself at `1em` and the local-svg factory defaults `fontSize="inherit"`, so giving the SLOT a
 *     font-size is what makes that `1em` resolve to 16px.
 *
 * The font-size is the one doing the work. `size-full` cannot size a MUI glyph: it is a Tailwind
 * utility and so sits in `@layer utilities`, while `SvgIcon`'s `1em` is UNLAYERED Emotion — and
 * unlayered beats layered whatever the specificity. Without it, a row icon resolved `1em` against
 * the row's own label type size and drew smaller than the 16px box it sits in.
 *
 * A caller's own `fontSize` / `sx` on the glyph still wins, because a direct declaration beats an
 * inherited one. That is deliberate, and it is why this is safe to change in one place: every call
 * site that pins its glyph keeps drawing exactly what it draws today.
 */
export const menuItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[length:16px]',
  '[&_svg]:size-full',
]);

/** The label. `min-w-0` releases the flex item's minimum content size so `truncate` can shrink it. */
export const menuItemLabelRecipe = cva(['min-w-0 truncate']);

/**
 * The column holding the label and, when there is one, the description.
 *
 * Always rendered, even for a single-line row, so the label's box is the same element in both cases
 * and a row cannot pick up different flex behaviour by gaining a description. On a row without one
 * the column collapses to the label's own line box, which is what the 36px cell already is.
 */
export const menuItemTextRecipe = cva(['flex min-w-0 flex-1 flex-col']);

/**
 * The description.
 *
 * EXTRAPOLATED — see `MenuOption.description`. Colour comes from the row through inheritance rather
 * than from a slot of its own, following `Alert`, where the title and the description are both
 * `--ds-alert-fg`.
 *
 * `text-body-md` is the scale's default reading size, chosen by what this line IS — a description —
 * rather than by matching the size it used to draw. It used to be a step smaller than the row's
 * label, on the reasoning that `Alert` separates its two lines by size and weight. The label is a
 * label style at the same size, so the two lines now differ by WEIGHT alone, which is the other
 * half of what `Alert` does and is what the library specifies for this line.
 *
 * The line box is unchanged, so a single-line description does not move the row. A description long
 * enough to WRAP is a different matter: it wraps inside the cap below, so a larger size can push it
 * onto another line and grow the row. That is accepted rather than worked around — nothing pins this
 * row's height, and the story that measures it asserts a floor.
 *
 * `max-w-[280px]` is what makes the line WRAP. The panel is `w-max`, so without a concrete width
 * the description's max-content width would simply widen the panel until the whole sentence fit on
 * one line — a ~70-character line producing a ~450px popover. 280px is a chosen number, not a
 * measured one: the drawn row is 220px (node 556:20713), and this is the next step up that reads as
 * a menu rather than a dialog. It applies here rather than on the panel so a menu WITHOUT
 * descriptions keeps sizing to its content exactly as before.
 */
export const menuItemDescriptionRecipe = cva(['text-body-md', 'max-w-[280px] whitespace-normal']);

/**
 * The group separator.
 *
 * EXTRAPOLATED — see the `.ds-menu` block header. Rendered as a bordered `<div>` rather than an
 * `<hr>`: Base UI's `Menu.Separator` sets `role="separator"` itself, and a UA-styled `<hr>` would
 * bring its own margins into a panel whose spacing is already specified.
 *
 * The negative inset is what makes the line span the full panel rather than stopping inside the
 * rows' padding — the legacy menu uses MUI's `variant="middle"` Divider, which insets it. The design
 * draws no separator at all, so this follows the panel's own border, which runs edge to edge.
 *
 * ── The extrapolation has since been CONFIRMED from elsewhere (2026-09-17) ──────────────────────
 *
 * The `Menu` set still draws no separator, so the note above stands. But the library has since
 * published a `Divider` component (node 1472:150575, ported to `components/Divider`), and its
 * description specifies this composition explicitly: full-bleed, "runs edge to edge rather than
 * inset", and "in Menu's Content slot: the separator sits above the row that OPENS a group, never
 * below the row that closes one, and never against the panel's own top padding". Both readings this
 * file arrived at independently — the negative inset and `separatorBefore`'s direction — are what
 * the design now says.
 *
 * What is NOT yet reconciled is the colour. The divider takes the translucent `--border-on-surface`
 * so one rule works on any surface; this slot takes `--border-secondary`, which is opaque and tuned
 * to the panel's own fill. Both measure inside the decorative band on this panel, so neither is
 * wrong today and nothing here changes on that basis — but if this separator is ever replaced by
 * the `Divider` component, that is the difference to expect.
 *
 * It MUST equal the panel's own horizontal padding, and that is a hard constraint rather than a
 * matter of taste. This read `-mx-[12px]` against the panel's old `px-[12px]`; the `List-Item`
 * rework took the panel to 8px of horizontal padding and left this at 12, so the line overhung the padding box by
 * 4px a side. The panel is `overflow-y-auto`, and CSS resolves a `visible` overflow on one axis to
 * `auto` when the other is not — so the overhang gave the panel a real horizontal scrollbar, whose
 * height then pushed the content past the panel's own height and produced a VERTICAL scrollbar
 * beside it. Two scrollbars on a 151px menu, from one stale number.
 *
 * `my-[8px]` (`x2`) is the group break. Deliberately larger than the 2px row gap, so a break
 * between groups reads as heavier than the air between two rows in one group.
 */
export const menuSeparatorRecipe = cva([
  '-mx-[8px] my-[8px] h-0 shrink-0',
  'border-0 border-t border-solid border-[var(--ds-menu-separator)]',
]);
